import { cn } from "@/lib/utils";
import { Card, CardHeader, CardProps, CardTitle } from "./card";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";
import { Skeleton } from "./skeleton";
import WidgetEmptyContent from "@components/widgets/partials/Empty";
import { ReactNode } from "react";
import { useI18n } from "@/contexts/i18nContext";
import { AnimatePresence, motion } from "motion/react";

export type WidgetProps = CardProps;

function Widget({
  children,
  className,
  variant = "muted",
  ...rest
}: WidgetProps) {
  return (
    <Card
      data-slot="widget"
      variant={variant}
      className={cn(
        "in-data-[slot=widget]:rounded-none in-data-[slot=widget]:*:data-[slot=card-header]:px-0 in-data-[slot=widget]:*:data-[slot=widget-content]:px-0",
        "@5xl/grid:in-data-[slot=widget]:*:data-[slot=card-header]:px-6 @5xl/grid:in-data-[slot=widget]:*:data-[slot=widget-content]:px-6 gap-3",
        className
      )}
      {...rest}
    >
      {children}
    </Card>
  );
}

function WidgetHeader({
  variant = "default",
  skeletonClassName,
  isLoading,
  children,
  ...rest
}: {
  isLoading?: boolean;
  variant?: WidgetProps["variant"];
  skeletonClassName?: string;
} & React.ComponentProps<"div">) {
  return (
    <CardHeader {...rest}>
      <AnimatePresence mode="popLayout">
        {isLoading ? (
          <motion.div
            className={cn("h-11 flex-1", skeletonClassName)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="loading"
          >
            <Skeleton variant={variant} className="size-full" />
          </motion.div>
        ) : (
          <motion.div
            className="flex items-start justify-between gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="content"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </CardHeader>
  );
}

function WidgetTitle({
  children,
  className,
  ...rest
}: React.ComponentProps<"div">) {
  return (
    <CardTitle
      className={cn("text-base font-semibold line-clamp-1", className)}
      {...rest}
    >
      {children}
    </CardTitle>
  );
}

type WidgetTrendVariant = "percent" | "percent-unit";

function WidgetTrend({
  variant = "percent",
  className,
  value,
  ...rest
}: React.ComponentProps<"p"> & {
  value: number | undefined | null;
  variant?: WidgetTrendVariant;
}) {
  const { formatPercent } = useI18n();
  const { t } = useLingui();

  const isPositive = value && value > 0;
  const isUnchanged = !value;
  const isEffectivelyUnchanged = value && !Math.floor(value * 100);
  const isOneOrMinusOne = value && Math.abs(Math.floor(value)) === 1;

  if (isUnchanged || isEffectivelyUnchanged) {
    return null;
  }

  return (
    <p className={cn("text-right text-xs text-balance", className)} {...rest}>
      {isPositive ? (
        <ArrowUpIcon className="inline -translate-y-px size-3.5" />
      ) : (
        <ArrowDownIcon className="inline -translate-y-px size-3.5" />
      )}{" "}
      {formatPercent(value, 0)}
      {variant === "percent"
        ? ""
        : isOneOrMinusOne
          ? t`-enhet`
          : t`-enheter`}{" "}
      <span className="text-muted-foreground">
        {" "}
        <Trans>mot förra perioden</Trans>
      </span>
    </p>
  );
}

function WidgetStat({
  children,
  className,
  change,
  variant,
  ...rest
}: React.ComponentProps<"div"> & {
  change: number | undefined | null;
  variant?: WidgetTrendVariant;
}) {
  return (
    <div className="flex flex-col items-end">
      <CardTitle className={cn("text-xl font-semibold", className)} {...rest}>
        {children}
      </CardTitle>
      <WidgetTrend variant={variant} value={change} />
    </div>
  );
}

function WidgetHeading({
  children,
  className,
  ...rest
}: React.ComponentProps<"h3">) {
  return (
    <h3
      className={cn("font-semibold text-2xl line-clamp-1", className)}
      {...rest}
    >
      {children}
    </h3>
  );
}

function WidgetLede({
  children,
  className,
  ...rest
}: React.ComponentProps<"p">) {
  return (
    <p
      className={cn("text-xs text-muted-foreground line-clamp-1", className)}
      {...rest}
    >
      {children}
    </p>
  );
}

function WidgetContent({
  variant = "default",
  children,
  className,
  isLoading,
  isEmpty,
}: {
  variant?: WidgetProps["variant"];
  isLoading?: boolean;
  isEmpty?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <AnimatePresence mode="popLayout">
      {isLoading ? (
        <motion.div
          className={cn("flex-1 px-6", className)}
          data-slot="widget-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          key="loading"
        >
          <Skeleton variant={variant} className={cn("size-full")} />
        </motion.div>
      ) : isEmpty ? (
        <motion.div
          className={cn("px-6 flex flex-col gap-1.5 flex-1", className)}
          data-slot="widget-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          key="empty"
        >
          <WidgetEmptyContent />
        </motion.div>
      ) : (
        <motion.div
          className={cn(
            "scrollbar-track-sidebar scrollbar-thumb-neutral-300 px-6 flex flex-col gap-1.5 flex-1",
            className
          )}
          data-slot="widget-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          key="content"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export {
  Widget,
  WidgetHeader,
  WidgetTitle,
  WidgetStat,
  WidgetHeading,
  WidgetLede,
  WidgetContent,
};
